import { useEffect, useState } from 'react';

const SELECTED_IMAGE_STORAGE_KEY = 'selectedImage';

const getImagePayload = (state) => {
    if (!state) {
        return null;
    }

    if (state.image) {
        return state.image;
    }

    if ('navigationImages' in state && !('id' in state)) {
        return null;
    }

    return state;
};

const getNavigationImages = (state) => (
    Array.isArray(state?.navigationImages)
        ? state.navigationImages.map((image) => ({
            ...image,
            id: String(image.id)
        }))
        : null
);

const getImageFromNavigation = (navigationImages, targetImageId) => {
    if (!Array.isArray(navigationImages) || !targetImageId) {
        return null;
    }

    return navigationImages.find((navImage) => navImage.id === String(targetImageId)) || null;
};

const saveSelectedImageState = (state) => {
    sessionStorage.setItem(SELECTED_IMAGE_STORAGE_KEY, JSON.stringify(state));
};

const useImageNavigationState = (locationState, imageId) => {
    const [image, setImage] = useState(getImagePayload(locationState));
    const [navigationImages, setNavigationImages] = useState(getNavigationImages(locationState));

    useEffect(() => {
        const nextNavigationImages = getNavigationImages(locationState);
        const nextImagePayload = getImagePayload(locationState)
            || getImageFromNavigation(nextNavigationImages, imageId);

        setImage((prev) => nextImagePayload || prev);
        setNavigationImages(nextNavigationImages);
    }, [imageId, locationState]);

    useEffect(() => {
        if (!locationState) {
            return;
        }

        saveSelectedImageState(locationState);
        setNavigationImages(getNavigationImages(locationState));
    }, [locationState]);

    useEffect(() => {
        if (image) {
            return;
        }

        const stored = sessionStorage.getItem(SELECTED_IMAGE_STORAGE_KEY);

        if (!stored) {
            return;
        }

        try {
            const parsedPayload = JSON.parse(stored);
            const storedNavigationImages = getNavigationImages(parsedPayload);
            const storedImagePayload = getImagePayload(parsedPayload)
                || getImageFromNavigation(storedNavigationImages, imageId);

            setImage(storedImagePayload);
            setNavigationImages(storedNavigationImages);
        } catch (err) {
            console.error('Failed to parse selectedImage from sessionStorage:', err);
        }
    }, [image, imageId]);

    return {
        image,
        setImage,
        navigationImages,
        setNavigationImages,
        saveSelectedImageState
    };
};

export default useImageNavigationState;
export { saveSelectedImageState };
